import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  Edit,
  Delete,
  Phone,
  Email,
  LocalHospital,
  Build,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import HouseCollectionEmptyState from '../components/HouseCollectionEmptyState';
import HouseCollectionGrid from '../components/HouseCollectionGrid';
import HouseCollectionHeader from '../components/HouseCollectionHeader';
import Layout from '../components/Layout';
import HousePageBackLink from '../components/HousePageBackLink';
import axios from '../api/axios';

interface Contact {
  id: string;
  name: string;
  type: 'emergency' | 'medical' | 'practical';
  phone?: string;
  email?: string;
  notes?: string;
}

interface ContactsPageProps {
  embedded?: boolean;
  isOwner?: boolean;
}

export const ContactsPage: React.FC<ContactsPageProps> = ({ embedded = false, isOwner = false }) => {
  const { id: houseId } = useParams<{ id: string }>();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'practical' as Contact['type'],
    phone: '',
    email: '',
    notes: '',
  });
  const { t } = useTranslation();

  const fetchContacts = useCallback(async () => {
    if (!houseId) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`/contacts?houseId=${houseId}`);
      setContacts(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('contacts.failedFetch'));
    } finally {
      setLoading(false);
    }
  }, [houseId, t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (houseId) {
      fetchContacts();
    }
  }, [houseId, fetchContacts]);

  const handleCreate = () => {
    setSelectedContact(null);
    setFormData({ name: '', type: 'practical', phone: '', email: '', notes: '' });
    setDialogOpen(true);
  };

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      type: contact.type,
      phone: contact.phone || '',
      email: contact.email || '',
      notes: contact.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (contactId: string) => {
    if (!window.confirm(t('contacts.deleteConfirm'))) return;

    try {
      await axios.delete(`/contacts/${contactId}`);
      fetchContacts();
    } catch (err: any) {
      setError(err.response?.data?.message || t('contacts.failedDelete'));
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        notes: formData.notes || undefined,
        houseId,
      };

      if (selectedContact) {
        await axios.put(`/contacts/${selectedContact.id}`, payload);
      } else {
        await axios.post('/contacts', payload);
      }

      setDialogOpen(false);
      fetchContacts();
    } catch (err: any) {
      setError(err.response?.data?.message || t('contacts.failedSave'));
    }
  };

  const getCategoryIcon = (type: string) => {
    switch (type) {
      case 'emergency':
        return <LocalHospital color="error" />;
      case 'medical':
        return <LocalHospital color="primary" />;
      case 'practical':
        return <Build color="action" />;
      default:
        return <Phone color="action" />;
    }
  };

  const getCategoryChip = (type: string) => {
    const colors = {
      emergency: 'error',
      medical: 'primary',
      practical: 'info',
    };
    return (
      <Chip
        label={t(`contacts.categories.${type}`)}
        color={colors[type as keyof typeof colors] as any}
        size="small"
      />
    );
  };

  const groupedContacts = contacts.reduce((acc, contact) => {
    if (!acc[contact.type]) {
      acc[contact.type] = [];
    }
    acc[contact.type].push(contact);
    return acc;
  }, {} as Record<string, Contact[]>);

  const content = (
    <Container maxWidth={embedded ? false : "lg"} sx={{ px: embedded ? 0 : undefined }}>
      {!embedded && <HousePageBackLink />}
      <HouseCollectionHeader
        title={t('contacts.title')}
        description={t('contacts.noContactsDescription')}
        actionLabel={isOwner ? t('contacts.addContact') : undefined}
        onAction={isOwner ? handleCreate : undefined}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : contacts.length === 0 ? (
        <HouseCollectionEmptyState
          title={t('contacts.noContacts')}
          description={t('contacts.noContactsDescription')}
          actionLabel={isOwner ? t('contacts.addFirst') : undefined}
          onAction={isOwner ? handleCreate : undefined}
        />
      ) : (
        <Box>
          {Object.entries(groupedContacts).map(([category, categoryContacts]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                {getCategoryIcon(category)}
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  {t(`contacts.categories.${category}`)}
                </Typography>
              </Stack>
              <HouseCollectionGrid
                spacing={2}
              >
                {categoryContacts.map((contact) => (
                  <Grid item xs={8} sm={6} md={4} key={contact.id} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                    <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getCategoryIcon(contact.type)}
                            <Typography variant="h6">{contact.name}</Typography>
                          </Box>
                          {isOwner && (
                            <Box>
                              <IconButton size="small" onClick={() => handleEdit(contact)}>
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDelete(contact.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ mb: 1.5 }}>
                          {getCategoryChip(contact.type)}
                        </Box>

                        {contact.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                            <Phone fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Link href={`tel:${contact.phone}`} underline="hover">
                              {contact.phone}
                            </Link>
                          </Box>
                        )}

                        {contact.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <Email fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Link href={`mailto:${contact.email}`} underline="hover">
                              {contact.email}
                            </Link>
                          </Box>
                        )}

                        {contact.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontStyle: 'italic' }}>
                            {contact.notes}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </HouseCollectionGrid>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedContact ? t('contacts.editTitle') : t('contacts.createTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t('common.fields.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('common.fields.category')}</InputLabel>
              <Select
                value={formData.type}
                label={t('common.fields.category')}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              >
                <MenuItem value="emergency">{t('contacts.categories.emergency')}</MenuItem>
                <MenuItem value="medical">{t('contacts.categories.medical')}</MenuItem>
                <MenuItem value="practical">{t('contacts.categories.practical')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={t('common.fields.phone')}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              sx={{ mb: 2 }}
              type="tel"
            />
            <TextField
              fullWidth
              label={t('common.fields.email')}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              sx={{ mb: 2 }}
              type="email"
            />
            <TextField
              fullWidth
              label={t('common.fields.notes')}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.actions.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedContact ? t('common.actions.update') : t('common.actions.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout title={t('contacts.layoutTitle')}>
      {content}
    </Layout>
  );
};

export default ContactsPage;
